import axios from "axios";
import { USER_LOCAL_STORAGE_KEY } from "@constants/Auth.constants.js";

const TAB_BASE_PATHS = {
  SALES: {
    data: "/control-data",
    pdf_sheet_data: "/pdf-sheet/sheet-lib",
    pdf_bundle_data: "/pdf-sheet/pdf-bundles",
  },
  COORDINATION: {
    data: "/coord-data",
    pdf_sheet_data: "/pdf-sheet/coord-sheet-lib",
    pdf_bundle_data: "/pdf-sheet/coord-pdf-bundles",
  },
  IFC: {
    data: "/ifc-data",
    pdf_sheet_data: "/pdf-sheet/ifc-sheet-lib",
    pdf_bundle_data: "/pdf-sheet/ifc-pdf-bundles",
  },
  HVAC: {
    data: "/hvac-data",
    pdf_sheet_data: "/pdf-sheet/hvac-sheet-lib",
    pdf_bundle_data: "/pdf-sheet/hvac-pdf-bundles",
  },
  DESIGN: {
    data: "/design-data",
    pdf_sheet_data: "/pdf-sheet/design-sheet-lib",
    pdf_bundle_data: "/pdf-sheet/design-pdf-bundles",
  },
};

export const generalRequest = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/`,
  withCredentials: true,
});

export const apiRequest = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

apiRequest.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      (error.response && error.response.status === 401) ||
      error.response.status === 403
    ) {
      console.warn("Session expired. Logging out...");
      localStorage.removeItem(USER_LOCAL_STORAGE_KEY);
      window.location.href = "/home/login";
    }
    return Promise.reject(error);
  },
);

/**
 * Authenticates a user by sending a login request to the server.
 *
 * @async
 * @param {string} username - The user's username or email address.
 * @param {string} password - The user's password.
 * @returns {Promise<number>} The HTTP status code of the login response.
 * @throws {Error} If the login request fails for any reason.
 *
 **/
export const loginAuth = async (username, password) => {
  try {
    const login_response = await apiRequest.post(`/auth/login`, {
      username,
      password,
    });
    return login_response.status;
  } catch (error) {
    throw new Error("Failed to login: " + error.message);
  }
};

/**
 * Authenticates a user by sending a logout request to the server.
 *
 * @async
 * @returns {Promise<Object>} The logout response from the server.
 * @throws {Error} If the logout request fails for any reason.
 *
 **/
export const logoutAuth = async () => {
  try {
    const logout_response = await apiRequest.post(`/auth/logout`);
    return logout_response.data;
  } catch (error) {
    throw new Error("Failed to logout: " + error.message);
  }
};

/**
 * Fetches a user profile by sending a profile request to the server.
 *
 * @async
 * @returns {Promise<string>} The encoded profile data as a string.
 * @throws {Error} If the profile request fails for any reason or if encoding fails.
 *
 **/
export const fetchProfile = async () => {
  try {
    const profileData_response = await apiRequest.get(`/auth/profile`);
    return profileData_response.data;
  } catch (error) {
    throw new Error("Failed to fetch profile: " + error.message);
  }
};

export const fetchRoleAccessProject = async (currentUserRole) => {
  try {
    let roleAccessProjectData_response = await apiRequest.get(
      `/role-access/${currentUserRole}`,
    );

    return roleAccessProjectData_response.data;
  } catch (error) {
    throw new Error(
      "Failed to fetch project granted for a role: " + error.message,
    );
  }
};

export const fetchProjectList = async (projectID) => {
  try {
    let projectListData_response;

    if (!projectID) {
      // no role → fetch all accessible projects
      projectListData_response = await apiRequest.get(
        `/master-data/master-projects`,
      );
    } else {
      // has role → fetch projects specific to that role
      projectListData_response = await apiRequest.get(
        `/master-data/master-projects/${projectID}`,
      );
    }

    return projectListData_response.data;
  } catch (error) {
    throw new Error("Failed to fetch project list: " + error.message);
  }
};

/**
 * Generic fetcher for tab-based resources.
 *
 * @async
 * @param {string} selectedTab - The tab name ("SALES" or "COORDINATION").
 * @param {string} resourcePath - The resource path with optional placeholders (e.g., "control-versions-data/{modelID}").
 * @param {Object} [params={}] - Optional params to replace placeholders in resourcePath.
 * @returns {Promise<Object>} - The data from the API.
 * @throws {Error} If the request fails or tab is invalid.
 */
const fetchData = async (selectedTab, resourcePath, params = {}) => {
  if (!selectedTab) throw new Error("Missing input parameter - selectedTab");

  const basePath = TAB_BASE_PATHS[selectedTab].data;
  if (!basePath) throw new Error(`Invalid tab selected: ${selectedTab}`);

  // Replace placeholders in resourcePath
  const path = Object.entries(params).reduce(
    (acc, [key, value]) => acc.replace(`{${key}}`, encodeURIComponent(value)),
    resourcePath,
  );

  try {
    const response = await apiRequest.get(`${basePath}/${path}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to fetch ${resourcePath}: ${error.message}`);
  }
};

/**
 * Generic fetcher for PDF endpoints (sheet library or bundles)
 *
 * @async
 * @param {string} selectedTab - "SALES" or "COORDINATION"
 * @param {"pdf_sheet_data"|"pdf_bundle_data"} type - Type of PDF endpoint
 * @param {string} resourcePath - Path with placeholders (e.g., "{modelID}/{selectedVersion}")
 * @param {Object} [params={}] - Placeholder values
 * @returns {Promise<Object>} - API response data
 * @throws {Error} If request fails or tab/type is invalid
 */
const fetchPDFData = async (selectedTab, type, resourcePath, params = {}) => {
  if (!selectedTab) throw new Error("Missing input parameter - selectedTab");
  if (!["pdf_sheet_data", "pdf_bundle_data"].includes(type))
    throw new Error(`Invalid PDF type: ${type}`);

  const basePath = TAB_BASE_PATHS[selectedTab]?.[type];
  if (!basePath) throw new Error(`Invalid tab selected: ${selectedTab}`);

  // Replace placeholders
  const path = Object.entries(params).reduce(
    (acc, [key, value]) => acc.replace(`{${key}}`, value),
    resourcePath,
  );

  try {
    const response = await apiRequest.get(`${basePath}/${path}`);
    return response.data;
  } catch (error) {
    throw new Error(`Failed to fetch ${type}: ${error.message}`);
  }
};

export const fetchAllModelImageData = async () => {
  try {
    const response = await apiRequest.get("image-data");
    return response.data;
  } catch (error) {
    throw new Error(`Failed to fetch model image data: ${error.message}`);
  }
};

export const fetchModelMenuData = (selectedTab) =>
  fetchData(selectedTab, "control-models-data");

export const fetchVersionOptionsData = (selectedTab, modelID) => {
  if (!modelID) throw new Error("Missing input parameter - modelID");
  return fetchData(selectedTab, "control-versions-data/{modelID}", { modelID });
};

export const fetchStyleOptionsData = (
  selectedTab,
  modelID,
  selectedVersion,
) => {
  if (!modelID || !selectedVersion)
    throw new Error("Missing input parameter - modelID/selectedVersion");
  return fetchData(
    selectedTab,
    "control-style-data/{modelID}/{selectedVersion}",
    {
      modelID,
      selectedVersion,
    },
  );
};

export const fetchDesignOptionsData = (selectedTab, modelID, selectedVersion) =>
  fetchData(selectedTab, "control-options-data/{modelID}/{selectedVersion}", {
    modelID,
    selectedVersion,
  });

export const fetchDependentConflictRules = (
  selectedTab,
  modelID,
  selectedVersion,
) =>
  fetchData(
    selectedTab,
    "dependent-conflict-rules/{modelID}/{selectedVersion}",
    {
      modelID,
      selectedVersion,
    },
  );

// Sheet library
export const fetchSheetLibraryData = (selectedTab, modelID, selectedVersion) =>
  fetchPDFData(selectedTab, "pdf_sheet_data", "{modelID}/{selectedVersion}", {
    modelID,
    selectedVersion,
  });

// PDF bundle
export const fetchPDFBundleData = (
  selectedTab,
  modelID,
  selectedVersion,
  orientation,
  style,
) =>
  fetchPDFData(
    selectedTab,
    "pdf_bundle_data",
    "{modelID}/{selectedVersion}/{orientation}/{style}",
    { modelID, selectedVersion, orientation, style },
  );

export const fetchAllPPOCodeListData = (
  selectedTab,
  modelID,
  selectedVersion,
) =>
  fetchData(selectedTab, "all-ppoCode-list/{modelID}/{selectedVersion}", {
    modelID,
    selectedVersion,
  });

export const fetchTopGroupPPOListData = (
  selectedTab,
  modelID,
  selectedVersion,
) =>
  fetchData(selectedTab, "mand-ppoCode-list/{modelID}/{selectedVersion}", {
    modelID,
    selectedVersion,
  });

export const fetchSheetTopGroupLibData = (
  selectedTab,
  modelID,
  selectedVersion,
) =>
  fetchData(selectedTab, "sheet-mand-ppo-options/{modelID}/{selectedVersion}", {
    modelID,
    selectedVersion,
  });

// === Redis cache clear (remains unchanged) ===
export const clearRedisCache = async () => {
  try {
    const response = await generalRequest.post("clear-redis");
    return response.status;
  } catch (error) {
    throw new Error("Failed to clear Redis cache: " + error.message);
  }
};

export default apiRequest;
